import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { ShieldCheck, UserPlus } from 'lucide-react'
import { fetchSuperAdmins, promoteSuperAdmin } from '../../api'
import { PageHeader } from '../../components/PageHeader'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Skeleton } from '../../components/ui/skeleton'
import { Card } from '../../components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table'
import { UserAvatar } from '../../components/UserAvatar'

export function SuperAdminsPage() {
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)

  const query = useQuery({ queryKey: ['admin', 'super-admins'], queryFn: fetchSuperAdmins })

  const promote = useMutation({
    mutationFn: () => promoteSuperAdmin(email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'super-admins'] })
      setEmail('')
    },
    onError: (err) => {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        setError('No existe ninguna cuenta con ese email — la persona tiene que registrarse primero.')
      } else {
        setError('No se pudo promover a esa cuenta.')
      }
    },
  })

  function handleSubmit(e: FormEvent): void {
    e.preventDefault()
    setError(null)
    promote.mutate()
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader icon={ShieldCheck} title="Super Admins" description="Quién más tiene acceso de plataforma como este." />

      <form onSubmit={handleSubmit} className="flex max-w-md items-end gap-2">
        <div className="flex-1">
          <Input
            type="email"
            required
            placeholder="Email de una cuenta ya registrada"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <Button type="submit" disabled={promote.isPending}>
          <UserPlus className="size-4" />
          Promover
        </Button>
      </form>
      {error && <p className="text-sm text-destructive">{error}</p>}

      <Card className="overflow-hidden py-0">
        {query.isLoading && (
          <div className="flex flex-col gap-3 p-5">
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-2/3" />
          </div>
        )}
        {query.data && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Email</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.data.map((admin) => (
                <TableRow key={admin.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2.5">
                      <UserAvatar name={admin.nombre} seed={admin.email} size="sm" />
                      {admin.nombre}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{admin.email}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  )
}
